import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Check,
  Clock,
  CheckCheck,
  Users,
  UserRound,
  Building2,
  Star,
  Eye,
  EyeOff,
  ArrowDownAZ,
  ArrowDownWideNarrow,
  Filter,
  FilterX,
  ListChecks,
  ChevronDown,
  Inbox,
  CheckSquare,
  Hash,
  Loader2,
  MessageSquare,
} from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import useWhatsApps from "../../hooks/useWhatsApps";
import useQueues from "../../hooks/useQueues";
import usePermissions from "../../hooks/usePermissions";
import ConfirmationModal from "../ConfirmationModal";
import api from "../../services/api";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import ListaDoChatInterno from "./ListaDoChatInterno";
import toastError from "../../errors/toastError";

// Botão de ferramenta da lista: só o ícone, com o nome aparecendo embaixo no
// hover — como na referência, onde o rótulo surge quando a mão chega.
// Traço entre grupos de ícones. Sem ele a barra é uma fileira de nove
// símbolos sem hierarquia: o que muda o RECORTE da lista fica igual ao que
// executa uma AÇÃO nela, e a pessoa lê tudo de uma vez ou nada.
const SeparadorDeFerramentas = () => (
  <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-border" />
);

const FerramentaDaLista = ({
  icone: Icone,
  rotulo,
  ativo,
  onClick,
  classe,
}) => (
  <div className="group/ferramenta relative">
    <button
      type="button"
      aria-label={rotulo}
      aria-pressed={ativo}
      onClick={onClick}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
        ativo
          ? "bg-primary/10 text-primary"
          : cn(
              "text-muted-foreground hover:bg-muted hover:text-foreground",
              classe,
            ),
      )}
    >
      <Icone className="h-[18px] w-[18px]" />
    </button>

    <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-0.5 -translate-x-1/2 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] font-medium text-background opacity-0 transition-opacity group-hover/ferramenta:opacity-100">
      {rotulo}
    </span>
  </div>
);

// Filtro de várias escolhas. Encapsula o padrão "rótulo + quantos escolhidos
// + lista com caixas de seleção", que se repete nos três filtros.
const FiltroDeLista = ({ rotulo, opcoes, selecionados, onChange }) => {
  const alternar = (id) =>
    onChange(
      selecionados.includes(id)
        ? selecionados.filter((s) => s !== id)
        : [...selecionados, id],
    );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-8 w-full items-center gap-1.5 rounded-md border px-2.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {rotulo}
          {selecionados.length > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums text-primary-foreground">
              {selecionados.length}
            </span>
          )}
          <ChevronDown className="ml-auto h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="scrollbar-discreta max-h-72 w-64 overflow-y-auto p-1"
      >
        {(opcoes || []).length === 0 ? (
          <p className="p-3 text-xs text-muted-foreground">
            Nada para filtrar.
          </p>
        ) : (
          opcoes.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => alternar(o.id)}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-muted"
            >
              <span
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                  selecionados.includes(o.id)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input",
                )}
              >
                {selecionados.includes(o.id) && <Check className="h-3 w-3" />}
              </span>
              {o.color && (
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: o.color }}
                />
              )}
              <span className="truncate">{o.name}</span>
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};

// As abas deixaram de ser por SITUAÇÃO (open/pending) e passaram a ser por
// DONO: "é meu", "é do meu setor", "não é de ninguém ainda". É a pergunta que
// quem atende realmente faz ao abrir a lista — "Atendendo" respondia
// "alguém está atendendo", que não ajuda a decidir o que fazer agora.
//
// Resolvidos continua fora das abas: é o painel "Fechados", alcançado pelo
// botão da barra de ferramentas. Grupos segue como aba própria porque grupo
// não tem dono nem entra no eixo meu/do setor — ficaria eternamente em
// "Aguardando", empurrando conversa de gente de verdade para baixo.
const ESTADOS = [
  {
    value: "minhas",
    rotulo: "Atendimento",
    icone: UserRound,
    // O recorte (só os meus, ou os do setor inteiro) deixou de ser uma aba e
    // virou o botão de prédio na barra de cima: eram quatro abas disputando a
    // largura da coluna, e "Do setor" é um filtro do que já está em
    // atendimento, não um lugar diferente.
    visao: "minhas",
    status: "open",
    ajuda: "Atendimentos em andamento",
    vazio: {
      titulo: "Nada na sua mão",
      texto: "Aceite uma conversa em Aguardando para começar.",
    },
  },
  {
    value: "pending",
    rotulo: "Aguardando",
    icone: Clock,
    status: "pending",
    ajuda: "Ninguém assumiu ainda",
    vazio: {
      titulo: "Nenhum cliente esperando",
      texto: "Novas conversas aparecem aqui assim que chegam.",
    },
  },
  {
    value: "grupos",
    rotulo: "Grupos",
    icone: Users,
    // Grupo não é "aceito" por ninguém: fica em aguardando para sempre. A
    // aba lista as duas situações, senão nunca mostra nada.
    // Também inclui status "closed": grupos que TÊM MENSAGENS MAS o switch
    // "Grupos viram atendimento" está desligado. Esses são gravados como
    // ticket fechado (histórico) para não cair na fila de atendimento, mas
    // PRECISAM aparecer aqui (com histórico completo, igual pede o usuário).
    // Sem o "closed" aqui, a aba "Grupos" mostrava só os que entram como atendimento.
    status: "open,pending,closed",
    ajuda: "Conversas em grupos do WhatsApp",
    somenteGrupos: true,
    vazio: {
      titulo: "Nenhuma conversa em grupo",
      texto: "Grupos aparecem aqui quando alguém escreve neles.",
    },
  },
  {
    // Chat da equipe na mesma coluna do atendimento: o recado do colega e a
    // fila do cliente são olhados no mesmo lugar. A conversa em si continua
    // na tela do chat interno — aqui é a porta de entrada.
    value: "interno",
    rotulo: "Interno",
    icone: MessageSquare,
    ajuda: "Conversas com a equipe",
    chatInterno: true,
  },
];

const RESOLVIDOS = {
  value: "closed",
  rotulo: "Resolvidos",
  icone: CheckCheck,
  status: "closed",
  ajuda: "Atendimentos encerrados",
  vazio: {
    titulo: "Nada resolvido por aqui",
    texto: "Os atendimentos encerrados ficam guardados nesta aba.",
  },
};

const TicketsManager = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { whatsApps } = useWhatsApps();
  // usePermissions() tem de vir ANTES de qualquer useState que use
  // "ticketScope". No código antigo ticketScope era usado na linha 249 mas
  // só definido na linha 296 → ReferenceError no console e tela branca
  // (erro runtime React de "variable used before declaration").
  const { ticketScope } = usePermissions();
  const { findAll: listarFilas } = useQueues();

  const userQueueIds = Array.isArray(user?.queues)
    ? user.queues
        .filter((q) => q && typeof q === "object" && q.id != null)
        .map((q) => q.id)
    : [];

  const [searchParam, setSearchParam] = useState("");
  // Com a busca sempre na tela, "estar buscando" é ter texto digitado —
  // não um modo que se liga e desliga.
  const buscando = searchParam.trim().length > 0;
  // Abre em "Minhas": o primeiro olhar da pessoa é para o que é dela.
  const [estado, setEstado] = useState("minhas");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  // Admin / supervisor (ticketScope === "company"): vem com "mostrar todos"
  // LIGADO de fábrica, então a aba Atendimento já abre mostrando o que todos
  // os atendentes estão fazendo. Para atendentes comuns (own/team/queues)
  // continua desligado: a pessoa vê só o que é dela, e liga o olho se quiser
  // ver o setor. O usuário pediu: "quem é admin e supervisor precisa ver
  // todos os atendimentos dos usuários" — esse é o ponto.
  const [showAllTickets, setShowAllTickets] = useState(
    ticketScope === "company",
  );
  // Filtro pessoal: mostra só o que a pessoa marcou com a estrela. Vale
  // dentro da aba em que ela está, não substitui a aba.
  const [soFavoritas, setSoFavoritas] = useState(false);
  // Período da aba "Resolvidos". Sem recorte ela cresce para sempre, e o que
  // interessa — o que fechou hoje — some no meio de meses de histórico.
  // Uma semana cobre o uso real: conferir o que a equipe encerrou.
  const [periodoResolvidos, setPeriodoResolvidos] = useState("7");
  const searchInputRef = useRef();

  const [contagens, setContagens] = useState({
    minhas: 0,
    setor: 0,
    pending: 0,
    grupos: 0,
  });

  // A aba de grupos só existe se alguma conexão estiver configurada para
  // atendê-los — do contrário é uma aba sempre vazia.
  const mostrarGrupos = (whatsApps || []).some((w) => w.showGroups);
  // As quatro abas são fixas: Atendimento, Aguardando, Grupos e Interno.
  //
  // Antes a de Grupos sumia quando nenhuma conexão tratava grupo como
  // atendimento — e quem procurava a aba achava que o sistema não tinha
  // grupos. Ela fica no lugar, e o estado vazio diz o que ligar.
  const abas = mostrarGrupos
    ? ESTADOS
    : ESTADOS.filter((e) => e.value !== "grupos");

  // Se a aba ativa for Grupos e ela foi removida (p.ex. o admin desligou o
  // Atender Grupos da conexão enquanto alguém estava na aba), volta o
  // usuário para a primeira aba disponível em vez de mostrar uma tela
  // vazia. Sem isso o painel inteiro de atendimentos parecia quebrado.
  useEffect(() => {
    const existe = abas.some((a) => a.value === estado);
    if (!existe) {
      setEstado(abas[0]?.value || "minhas");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarGrupos]);

  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds);

  // Filas que este usuário pode filtrar.
  //
  // Quem não tem fila vinculada — o administrador, quase sempre — ficava com
  // a seleção vazia, e a lista traduzia isso em "só ticket SEM fila": todo
  // atendimento que ganhava fila sumia da tela, e só voltava ligando o olho
  // do "mostrar todos". O grupo com fila fixa e o ticket recém-aceito
  // desapareciam pelo mesmo motivo.
  //
  // Sem fila própria, e podendo ver a empresa inteira, "nenhuma" passa a
  // significar "todas": as filas da empresa entram como opção do funil e
  // como seleção inicial. Quem é de fila (escopo "queues"/"team"/"own")
  // continua exatamente como antes.
  const [filasDaEmpresa, setFilasDaEmpresa] = useState(null);
  const semFilaPropria = userQueueIds.length === 0 && ticketScope === "company";

  useEffect(() => {
    if (!semFilaPropria || filasDaEmpresa) return;

    let vivo = true;

    listarFilas()
      .then((filas) => {
        if (!vivo) return;
        const lista = Array.isArray(filas) ? filas : [];
        setFilasDaEmpresa(lista);
        // Só define a seleção inicial se o usuário ainda não mexeu no funil.
        setSelectedQueueIds((atual) =>
          atual.length ? atual : lista.map((f) => f.id),
        );
      })
      .catch(() => setFilasDaEmpresa([]));

    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semFilaPropria]);

  const filasDoFiltro = semFilaPropria
    ? filasDaEmpresa || []
    : user?.queues || [];

  // "Todos" começa desligado para todo mundo, inclusive administrador: a
  // lista abre com os atendimentos de quem entrou, e ver os dos outros é uma
  // escolha de quem clica no botão. Antes o admin caía direto na fila
  // inteira da empresa e não tinha como enxergar só o que era dele.

  // "abertos" mostra as abas Atendendo/Aguardando/Grupos; "fechados" mostra
  // só os resolvidos, como na referência.
  const [painel, setPainel] = useState("abertos");
  const [ordemAZ, setOrdemAZ] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [filtroTags, setFiltroTags] = useState([]);
  const [filtroUsuarios, setFiltroUsuarios] = useState([]);
  const [filtroConexoes, setFiltroConexoes] = useState([]);
  const [etiquetas, setEtiquetas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState("");
  // Período (dd/mm/aaaa) e o atalho para ir direto a um atendimento pelo
  // número — é assim que a equipe se refere a ele no dia a dia.
  const [filtroDataInicio, setFiltroDataInicio] = useState("");
  const [filtroDataFim, setFiltroDataFim] = useState("");
  const [numeroDoTicket, setNumeroDoTicket] = useState("");
  // Agrupar a aba de encerrados por cliente. Fica na escolha de quem usa:
  // quem precisa auditar chamado por chamado desliga e vê todos.
  // Não lidas do chat da equipe, para o número na aba Interno.
  const [naoLidasInternas, setNaoLidasInternas] = useState(0);
  // "Do setor": mostra na aba Atendimento o que está com colegas das minhas
  // filas. Lembrado entre sessões — é preferência de como a pessoa trabalha.
  const [verSetor, setVerSetor] = useLocalStorage("verDoSetor", false);
  const [agruparEncerrados, setAgruparEncerrados] = useLocalStorage(
    "agruparEncerrados",
    true,
  );
  const [indoParaTicket, setIndoParaTicket] = useState(false);

  // As opções dos filtros só são buscadas quando os filtros aparecem: quem
  // nunca abre a gaveta não paga por ela.
  useEffect(() => {
    if (!filtrosAbertos || etiquetas.length || usuarios.length) return;

    (async () => {
      try {
        const [t, u] = await Promise.all([api.get("/tags"), api.get("/users")]);
        setEtiquetas(t.data?.tags || t.data || []);
        setUsuarios(u.data?.users || []);
      } catch (err) {
        // Sem as listas os filtros abrem vazios; não vale derrubar a tela.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosAbertos]);
  const temFiltro =
    filtroTags.length > 0 ||
    filtroUsuarios.length > 0 ||
    filtroConexoes.length > 0 ||
    Boolean(filtroDataInicio) ||
    Boolean(filtroDataFim);

  const limparFiltros = () => {
    setFiltroTags([]);
    setFiltroUsuarios([]);
    setFiltroConexoes([]);
    setFiltroDataInicio("");
    setFiltroDataFim("");
    setNumeroDoTicket("");
  };

  // Confere antes de navegar: mandar direto para /tickets/<n> de um número
  // que não existe (ou de outra empresa) larga a pessoa numa tela de erro.
  const irParaTicket = async () => {
    const numero = Number(numeroDoTicket);

    if (!numero) return;

    setIndoParaTicket(true);

    try {
      await api.get(`/tickets/${numero}`);
      setNumeroDoTicket("");
      navigate(`/tickets/${numero}`);
    } catch {
      toast.error(`Atendimento #${numero} não encontrado.`);
    }

    setIndoParaTicket(false);
  };

  const [confirmarFecharTodos, setConfirmarFecharTodos] = useState(false);

  const fecharTodos = async () => {
    try {
      const { data } = await api.put("/tickets/close-all");
      toast.success(
        data?.encerrados
          ? `${data.encerrados} atendimento(s) encerrado(s).`
          : "Nenhum atendimento em andamento.",
      );
    } catch (err) {
      toastError(err);
    }
  };

  // O timer fica num ref, não numa variável do corpo do componente: aquela
  // nascia de novo a cada render, então o clearTimeout limpava `undefined` e
  // a busca não era adiada — cada tecla disparava uma consulta de tickets.
  const searchTimeout = useRef();

  const handleSearch = (e) => {
    const termo = e.target.value.toLowerCase();
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearchParam(termo), 500);
  };

  // Digitar e sair da tela deixaria o timer pendente escrevendo estado em
  // componente desmontado.
  useEffect(() => () => clearTimeout(searchTimeout.current), []);

  const atualizarContagem = (chave) => (valor) =>
    setContagens((anterior) =>
      anterior[chave] === valor ? anterior : { ...anterior, [chave]: valor },
    );

  const estadoAtual = ESTADOS.find((e) => e.value === estado);

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-background">
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
      />

      {/* Busca, com o interruptor que amplia a procura para o conteúdo das
          mensagens — é mais lenta, então fica desligada por padrão. */}
      <div className="flex shrink-0 items-center gap-2 px-3 pb-1.5 pt-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Buscar atendimento e mensagens"
            type="search"
            onChange={handleSearch}
            className="h-9 rounded-full pl-9"
          />
        </div>

        <button
          type="button"
          aria-label={filtrosAbertos ? "Esconder filtros" : "Mostrar filtros"}
          title={filtrosAbertos ? "Esconder filtros" : "Mostrar filtros"}
          aria-pressed={filtrosAbertos}
          onClick={() => setFiltrosAbertos((v) => !v)}
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors",
            filtrosAbertos
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {filtrosAbertos ? (
            <Filter className="h-4 w-4" />
          ) : (
            <FilterX className="h-4 w-4" />
          )}
        </button>
      </div>

      {filtrosAbertos && (
        <div className="shrink-0 space-y-2 border-b px-3 pb-3">
          <FiltroDeLista
            rotulo="Etiquetas"
            opcoes={etiquetas}
            selecionados={filtroTags}
            onChange={setFiltroTags}
          />
          <FiltroDeLista
            rotulo="Conexões"
            opcoes={whatsApps}
            selecionados={filtroConexoes}
            onChange={setFiltroConexoes}
          />
          <div className="space-y-1">
            <p className="text-[11px] font-medium leading-none text-muted-foreground px-1">
              Departamento
            </p>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="h-8 text-xs w-full">
                <SelectValue placeholder="Todos os departamentos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Todos os departamentos</SelectItem>
                {(departments || []).map((d) => (
                  <SelectItem key={d.id} value={String(d.id)}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Can
            role={user.profile}
            perform="tickets-manager:showall"
            yes={() => (
              <FiltroDeLista
                rotulo="Atendentes"
                opcoes={usuarios}
                selecionados={filtroUsuarios}
                onChange={setFiltroUsuarios}
              />
            )}
          />

          {/* Período de abertura. Dois campos, e não um só: "de tal dia até
              tal dia" é como se pede um relatório de atendimento. */}
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="Abertos a partir de"
              title="Abertos a partir de"
              value={filtroDataInicio}
              onChange={(e) => setFiltroDataInicio(e.target.value)}
              className="h-9 flex-1"
            />
            <Input
              type="date"
              aria-label="Abertos até"
              title="Abertos até"
              value={filtroDataFim}
              onChange={(e) => setFiltroDataFim(e.target.value)}
              className="h-9 flex-1"
            />
          </div>

          {/* Ir direto ao atendimento pelo número. A busca acima também acha
              pelo número, mas quem já sabe qual é quer abrir, não procurar. */}
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              irParaTicket();
            }}
          >
            <div className="relative flex-1">
              <Hash className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={numeroDoTicket}
                onChange={(e) =>
                  setNumeroDoTicket(e.target.value.replace(/\D/g, ""))
                }
                placeholder="Ir para ticket..."
                inputMode="numeric"
                aria-label="Ir para o atendimento pelo número"
                className="h-9 pl-8"
              />
            </div>
            <Button
              type="submit"
              variant="secondary"
              size="sm"
              className="h-9"
              disabled={!numeroDoTicket || indoParaTicket}
            >
              {indoParaTicket ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Ir"
              )}
            </Button>
          </form>

          {temFiltro && (
            <button
              type="button"
              onClick={limparFiltros}
              className="mx-auto block text-xs font-medium text-primary hover:underline"
            >
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Barra de ferramentas, em três grupos separados por um traço:
          o que muda o RECORTE (todos, do setor), o que é AÇÃO (novo,
          favoritas, resolver todos) e o que troca o PAINEL (abertos,
          fechados, ordem). Antes eram nove ícones em fileira, todos com o
          mesmo peso. */}
      <div className="mx-3 mb-2 flex shrink-0 items-center gap-0.5 rounded-lg border bg-card px-1.5 py-1 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => (
            <FerramentaDaLista
              icone={showAllTickets ? Eye : EyeOff}
              rotulo="Todos"
              ativo={showAllTickets}
              onClick={() => setShowAllTickets((v) => !v)}
            />
          )}
        />

        {/* Do setor: era uma aba e virou filtro. Ligado, a aba Atendimento
            mostra também o que está com colegas das suas filas — é o mesmo
            "em atendimento", visto de mais perto ou de mais longe. */}
        <FerramentaDaLista
          icone={Building2}
          rotulo="Do setor"
          ativo={verSetor}
          onClick={() => setVerSetor((v) => !v)}
        />

        <SeparadorDeFerramentas />

        <FerramentaDaLista
          icone={Plus}
          rotulo="Novo"
          onClick={() => setNewTicketModalOpen(true)}
        />

        <FerramentaDaLista
          icone={Star}
          rotulo="Favoritas"
          ativo={soFavoritas}
          classe="text-amber-500"
          onClick={() => setSoFavoritas((v) => !v)}
        />

        <FerramentaDaLista
          icone={ListChecks}
          rotulo="Resolver todos"
          classe="text-emerald-600 dark:text-emerald-400"
          onClick={() => setConfirmarFecharTodos(true)}
        />

        <SeparadorDeFerramentas />

        <FerramentaDaLista
          icone={Inbox}
          rotulo="Abertos"
          ativo={painel === "abertos"}
          onClick={() => setPainel("abertos")}
        />

        <FerramentaDaLista
          icone={CheckSquare}
          rotulo="Fechados"
          ativo={painel === "fechados"}
          onClick={() => setPainel("fechados")}
        />

        <FerramentaDaLista
          icone={ordemAZ ? ArrowDownAZ : ArrowDownWideNarrow}
          rotulo={ordemAZ ? "Crescente" : "Decrescente"}
          ativo={ordemAZ}
          onClick={() => setOrdemAZ((v) => !v)}
        />

        <div className="ml-auto">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                Filas
                {selectedQueueIds.length > 0 && (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-semibold tabular-nums">
                    {selectedQueueIds.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-3">
              <p className="mb-1.5 text-xs font-medium">Mostrar filas</p>
              <TicketsQueueSelect
                className="w-full"
                selectedQueueIds={selectedQueueIds}
                userQueues={filasDoFiltro}
                onChange={(values) => setSelectedQueueIds(values)}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Abas em pílula, como o resto da interface (shadcn Tabs).
          O sublinhado fino não dizia onde uma aba termina e a outra começa —
          quatro palavras enfileiradas pareciam uma frase. Aqui a escolhida
          ganha uma pastilha com fundo próprio e sombra, e o trilho cinza
          delimita cada célula. Só existem no painel de abertos. */}
      <div
        className={cn(
          "shrink-0 border-b px-2 pb-2",
          painel !== "abertos" && "hidden",
        )}
      >
        <div
          role="tablist"
          aria-label="Atendimento, aguardando, grupos e chat interno"
          className="flex gap-0.5 rounded-lg bg-muted p-1"
        >
          {abas.map((item) => {
            const ativo = estado === item.value;
            // A aba Interno conta as não lidas do chat da equipe; as demais,
            // os atendimentos daquele recorte.
            const contagem = item.chatInterno
              ? naoLidasInternas
              : contagens[item.value];
            return (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={ativo}
                title={item.ajuda}
                onClick={() => setEstado(item.value)}
                className={cn(
                  // O rótulo precisa caber INTEIRO em quatro abas num celular
                  // de 360px — sobram ~90px por aba, e "ATENDIMENTO" em 11px
                  // maiúsculo com espaçamento não passa. Na tela estreita o
                  // texto perde o espaçamento extra e encolhe um ponto; a
                  // partir de sm volta ao tamanho de antes.
                  //
                  // O contador saiu de junto do ícone (onde precisava de um
                  // vão de 12px para não colar na primeira letra) e foi para o
                  // canto do botão: são 10px de largura recuperados por aba.
                  // Cada aba tem ~91px: a coluna de conversas é estreita e são
                  // quatro. Com ícone e maiúsculas, "ATENDIMENTO" precisava de
                  // 113px e invadia a aba vizinha.
                  //
                  // Sem ícone, em caixa normal e com o contador ao lado, o
                  // rótulo cabe inteiro na própria célula — e é o desenho da
                  // referência: "Atendimento 3 | Aguardando | Grupos | Interno".
                  // `px-0.5`: com `px-1` a aba de rótulo mais longo ("Aguardando"
                  // com contador) passava 2px do próprio espaço e o
                  // `overflow-hidden` comia a borda da bolinha do contador.
                  "relative flex min-w-0 flex-1 items-center justify-center gap-1 overflow-hidden rounded-md px-0.5 py-1.5 text-[11px] font-semibold tracking-normal transition-all",
                  ativo
                    ? "bg-background text-foreground shadow-sm ring-1 ring-black/[0.04] dark:ring-white/10"
                    : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
                )}
              >
                {/* Sem `truncate`: cortar "Atendimento" em "Atendim…" não
                    poupa espaço e só esconde a palavra. */}
                <span className="whitespace-nowrap">{item.rotulo}</span>

                {/* O contador ao lado, e não pendurado sobre o ícone: ali ele
                    cobria a primeira letra quando passava de 9. */}
                {/* O contador acompanha o estado da aba: cheio na escolhida,
                    discreto nas outras — senão quatro bolinhas coloridas
                    disputam a atenção e nenhuma diz nada. */}
                {contagem > 0 && (
                  <span
                    className={cn(
                      "flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[9px] font-bold tabular-nums",
                      ativo
                        ? "bg-primary text-primary-foreground"
                        : "bg-primary/15 text-primary",
                    )}
                  >
                    {contagem}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <ConfirmationModal
        title="Encerrar todos os atendimentos?"
        open={confirmarFecharTodos}
        onClose={() => setConfirmarFecharTodos(false)}
        onConfirm={fecharTodos}
      >
        Todas as conversas em andamento que você enxerga passam para Resolvidos.
        As mensagens continuam salvas.
      </ConfirmationModal>

      {/* Lista */}
      <div className="relative min-h-0 flex-1 border-t">
        {buscando ? (
          <TicketsList
            searchParam={searchParam}
            showAll={ticketScope === "company"}
            selectedQueueIds={selectedQueueIds}
            whatsApps={whatsApps}
            vazio={{
              titulo: "Nenhum resultado",
              texto: "Tente outro nome, número ou trecho da conversa.",
            }}
          />
        ) : (
          <>
            {/* Recorte de período: fica só no painel de fechados, que é o que
                acumula. Nas abas de abertos não faz sentido — o que está
                aberto interessa independentemente da idade. */}
            {painel === "fechados" && (
              <div className="absolute inset-x-0 top-0 z-10 flex items-center gap-2 border-b bg-background px-3 py-2">
                <span className="text-xs text-muted-foreground">Mostrar</span>
                {[
                  { v: "7", r: "7 dias" },
                  { v: "30", r: "30 dias" },
                  { v: "90", r: "90 dias" },
                  { v: "todos", r: "Tudo" },
                ].map((op) => (
                  <button
                    key={op.v}
                    type="button"
                    onClick={() => setPeriodoResolvidos(op.v)}
                    className={cn(
                      "rounded-md px-2 py-1 text-xs transition-colors",
                      periodoResolvidos === op.v
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {op.r}
                  </button>
                ))}

                {/* Agrupar por cliente. Ligado, a aba mostra uma linha por
                    contato — o atendimento mais recente dele. Desligado,
                    mostra chamado por chamado, para quem precisa auditar. */}
                <button
                  type="button"
                  onClick={() => setAgruparEncerrados((v) => !v)}
                  aria-pressed={agruparEncerrados}
                  title={
                    agruparEncerrados
                      ? "Mostrando um por cliente — clique para ver todos os chamados"
                      : "Mostrando todos os chamados — clique para agrupar por cliente"
                  }
                  className={cn(
                    "ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors",
                    agruparEncerrados
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  <UserRound className="h-3 w-3" />
                  Por cliente
                </button>
              </div>
            )}
            {(painel === "abertos" ? abas : [RESOLVIDOS]).map((item) => (
            <div
              key={item.value}
              className={cn(
                "absolute inset-0",
                // A barra de período ocupa o topo: sem empurrar a lista, ela
                // fica por cima do primeiro atendimento.
                painel === "fechados" && "top-[41px]",
                painel === "abertos" && estado !== item.value && "invisible",
              )}
            >
              {item.chatInterno ? (
                <div className="h-full overflow-y-auto">
                  <ListaDoChatInterno aoContar={setNaoLidasInternas} />
                </div>
              ) : (
              <TicketsList
                status={item.status}
                visao={
                  ticketScope === "company" &&
                  showAllTickets &&
                  (item.visao === "minhas" || item.value === "minhas")
                    ? undefined
                    : item.value === "minhas" && verSetor
                      ? "setor"
                      : item.visao
                }
                ultimosDias={
                  item.value === "closed" && periodoResolvidos !== "todos"
                    ? periodoResolvidos
                    : undefined
                }
                agruparPorContato={item.value === "closed" && agruparEncerrados}
                onlyFavorites={soFavoritas}
                showAll={
                  item.value === "closed"
                    ? ticketScope === "company"
                    : showAllTickets
                }
                ordemAZ={ordemAZ}
                tagIds={filtroTags}
                userIds={filtroUsuarios}
                whatsappIds={filtroConexoes}
                whatsApps={whatsApps}
                departmentId={departmentFilter}
                dataInicio={filtroDataInicio}
                dataFim={filtroDataFim}
                selectedQueueIds={selectedQueueIds}
                isGroup={item.somenteGrupos ? "true" : "false"}
                vazio={
                  item.somenteGrupos && !mostrarGrupos
                    ? {
                        titulo: "Grupos não estão virando atendimento",
                        texto:
                          "Ligue “Grupos viram atendimento” na conexão para as conversas de grupo aparecerem aqui.",
                      }
                    : item.vazio
                }
                updateCount={
                  item.value === "closed"
                    ? undefined
                    : atualizarContagem(item.value)
                }
              />
              )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default TicketsManager;
